import { Tooltip } from './Tooltip';

/**
 * ColumnTooltip — a small `?` circle button placed in a table column header
 * that, on hover or keyboard focus, explains what the column's values mean
 * (5d-3b UAT Fix 2). Built on the existing {@link Tooltip} so it inherits the
 * dark-card styling, 200ms show delay, focus/blur handling and
 * aria-describedby wiring used elsewhere in the portal.
 *
 * Used on the Users page (Staff + Subscribers tabs) and the Customers list
 * page — a shared component because it appears in 3+ places.
 *
 * Icon: /help-circle.svg (blue filled circle, bold white ?) served as a static
 * asset from public/. 20×20px. Simpler and more reliable than inline SVG.
 */
export interface ColumnTooltipItem {
  /** The column value, e.g. "Active" or "Super Admin". */
  term: string;
  /** One-line explanation of that value. */
  desc: string;
}

export function ColumnTooltip({
  columnName,
  items,
}: {
  columnName: string;
  items: ColumnTooltipItem[];
}) {
  return (
    <Tooltip
      title={columnName}
      variant="info"
      aria={`${columnName} column — ${items.map((i) => `${i.term}: ${i.desc}`).join('. ')}`}
      anchorClassName="portal-col-help-anchor"
      content={
        <dl className="portal-col-tooltip-list">
          {items.map((i) => (
            <div key={i.term} className="portal-col-tooltip-row">
              <dt>{i.term}</dt>
              <dd>{i.desc}</dd>
            </div>
          ))}
        </dl>
      }
    >
      <button
        type="button"
        className="portal-col-help-btn"
        aria-label={`About the ${columnName} column`}
        onClick={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') e.currentTarget.blur();
        }}
      >
        <img
          src="/help-circle.svg"
          width="20"
          height="20"
          alt=""
          aria-hidden="true"
          className="portal-col-help-icon"
        />
      </button>
    </Tooltip>
  );
}
